export default {
  pageTitle: "Sender Domain",
  pageDescription:
    "Send invoices, receipts, and reminders from your own verified domain.",
  menuLabel: "Sender Domain",
  statusBadge: {
    locked: "Add-on inactive",
    pending: "Pending verification",
    verified: "Verified",
    failed: "Verification failed",
    notConfigured: "Not configured",
    cleanupPending: "Cleanup pending",
  },
  loading: "Loading sender domain settings…",
  locked: {
    title: "Custom Sender Domain",
    description:
      "Send invoices, receipts, and reminders from your own verified domain (e.g. mail.idextrus.com). Eliminates spam-folder issues caused by sender-domain mismatch and removes Invup branding from your customer-facing emails.",
    benefitDeliverability: "Better deliverability — fewer spam-folder issues",
    benefitBranding: "Brand alignment — emails come from your domain",
    benefitWhiteLabel: "White-label emails — no Invup branding",
    benefitCompliance: "Custom compliance footer — your address, your name",
    cta: "Activate Custom Sender Domain ($15/mo)",
    pricingNote: "Billed monthly. Pro-rated at activation.",
  },
  needsConfig: {
    placeholder:
      "Configure your compliance address and sender domain below to start sending white-labeled email.",
    complianceAddressHeading: "Compliance address",
    complianceAddressDescription:
      "Set the postal address that appears in your white-labeled email footer.",
    domainEntryHeading: "Sender domain",
    domainEntryDescription:
      "Choose the subdomain we'll use as the From address on your customer emails.",
    domainEntryDisabledMessage:
      "Save your compliance address above before configuring a sender domain.",
  },
  domainEntry: {
    fieldDomainLabel: "Sender domain",
    fieldDomainHelp:
      "Use a subdomain you control (e.g., mail.yourcompany.com). Avoid your root domain so Invup mail can't affect your other email.",
    fieldDomainInvalid:
      "Enter a valid hostname (e.g., mail.example.com). Letters, digits, hyphens, and at least one dot allowed; no spaces.",
    fieldLocalPartLabel: "Mailbox name",
    fieldLocalPartHelp:
      "This is the part before @, e.g. invoices for invoices@mail.idextrus.com.",
    fieldLocalPartInvalid:
      "Only letters, digits, dots, hyphens, and underscores are allowed.",
    fieldLocalPartReserved: "This mailbox name is reserved.",
    submitLabel: "Save sender domain",
  },
  dnsRecords: {
    columnRecord: "Record",
    columnType: "Type",
    columnHost: "Host",
    columnValue: "Value",
    columnStatus: "Status",
    dkimLabel: "DKIM",
    returnPathLabel: "Return-Path",
    statusPass: "Passed",
    statusFail: "Failed",
    statusUnchecked: "Not yet checked",
    copyAria: "Copy to clipboard",
    copied: "Copied",
  },
  verification: {
    helpBanner:
      "Add these two DNS records at your DNS provider (e.g., Cloudflare, Route 53, GoDaddy). DNS changes can take 5–10 minutes to propagate. After saving, click Verify below.",
    subHelp:
      "If verification fails repeatedly: double-check that you copied the values exactly (no trailing spaces or quotes), wait 10 minutes for DNS propagation, then try again.",
    failureDetailHeadline: "Verification failed for these records:",
    failureDetailDkim:
      "DKIM TXT record didn't match. Check the host and value at your DNS provider.",
    failureDetailReturnPath:
      "Return-Path CNAME record didn't match. Check the host and value at your DNS provider.",
    verifyButton: "Verify",
    replaceButton: "Replace domain",
    removeButton: "Remove domain",
  },
  complianceSummary: {
    headline: "Compliance address",
    description:
      "Shown in the footer of every invoice, receipt, and reminder you send.",
    edit: "Edit",
    closeEdit: "Close",
  },
  localPart: {
    headline: "Sender address",
    description:
      "The mailbox name before @. Changing it won't affect your DNS records.",
    edit: "Edit",
    closeEdit: "Close",
    fieldLocalPartLabel: "Mailbox name",
    fieldLocalPartHelp:
      "The part before @. Your sender address uses the {{domain}} domain.",
    submitLabel: "Save mailbox name",
    updateSuccess: "Mailbox name updated.",
  },
  verified: {
    headline: "Your sender domain is verified",
    description:
      "Your invoice, receipt, and reminder emails now send from your own domain.",
    fieldDomain: "Domain",
    fieldSenderAddress: "Sender address",
    fieldDkimVerifiedAt: "DKIM verified at",
    fieldReturnPathVerifiedAt: "Return-Path verified at",
    replaceButton: "Replace domain",
    removeButton: "Remove domain",
  },
  replaceModal: {
    title: "Replace sender domain",
    confirmMessage:
      "Replacing your domain will remove the current DNS records and require you to set up new ones at your DNS provider. Continue?",
    entryMessage: "Enter the new domain you'd like to use.",
    continueButton: "Continue",
    submitLabel: "Replace domain",
  },
  removeModal: {
    title: "Remove sender domain",
    message:
      "Removing your domain means invoices will go back to sending from invup.me. Your add-on will continue to be billed unless removed. Continue?",
    confirmButton: "Remove domain",
  },
  cleanupPending: {
    bannerHeadline: "Remove these DNS records",
    bannerBody:
      "The Custom Sender Domain add-on is no longer active for {{domain}}, but the DKIM and Return-Path records are still pointing at Invup's mail provider. Remove them to avoid third-party hijack risk.",
    deadline: "Records will stop being held by Invup on {{deadline}}.",
    recordsHeading: "DNS records to remove",
    whyItMatters:
      "If these records are left in place after we release the domain, someone else could register the same domain with our mail provider and send signed email that appears to come from you.",
    reactivateLink: "Reactivate add-on",
    releaseButton: "I have removed the records",
    confirmTitle: "Release sender domain?",
    confirmMessage:
      "This will immediately release the domain we're holding for you. Only do this once you've removed the DKIM and Return-Path records from your DNS provider.",
    confirmButton: "Release slot",
  },
  configured: {
    placeholder: "Sender domain configured.",
  },
  complianceAddress: {
    legalTitle: "Why we need this",
    legalBody:
      "When you send invoices from your own domain, you become the legal sender of those messages under anti-spam law. Canada's Anti-Spam Legislation (CASL, s. 6(2)(b)) and the U.S. CAN-SPAM Act (15 U.S.C. § 7704(a)(5)(A)(iii)) require commercial electronic messages to include a valid physical postal address of the sender. We display this address in the email footer of every invoice, receipt, and reminder you send.",
    fieldCompanyName: "Company name",
    fieldCompanyNameHelp:
      "Optional. Defaults to your organization name if left blank.",
    fieldAddressLine1: "Street address",
    fieldAddressLine2: "Apt / Suite / Other",
    fieldCity: "City",
    fieldRegion: "State / Province",
    fieldPostalCode: "ZIP / Postal code",
    fieldCountry: "Country",
    submitLabel: "Save compliance address",
    updateSuccess: "Compliance address updated.",
  },
};
